import * as React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CardBase, GlassTable } from "../../../components/global";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { Talent } from "../../../types/users";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import TalentDrawer from "../../talent/TalentDrawer";
import {
  useAcceptBidMutation,
  useHireTalentMutation,
} from "../../../services/projectJob.service";
import dayjs from "dayjs";
import {
  NegotiationStatus,
  Project,
  ProjectResource,
  ProjectStatus,
} from "../../../types/project";
import ConfirmModal from "../../../components/ConfirmModal";
import { toast } from "react-toastify";
import { getTalentBidStatus } from "../../../utils/projectHelper";
import ModalInvite from "../ModalInvite";
import { formatWithTimezone } from "../../../utils/dateHelper";

const TableCancel = ({
  project,
  bids,
}: {
  project: Project;
  bids: Array<ProjectResource>;
}) => {
  const canceledJobs = bids.filter(
    (bid) => bid.status == NegotiationStatus.CANCELED
  );
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const user = useCurrentUser();
  const [selectedTalent, setSelectedTalent] = React.useState<Talent | null>(
    null
  );
  const [hireModal, setHireModal] = React.useState<{
    visible: boolean;
    job: ProjectResource | null;
  }>({
    visible: false,
    job: null,
  });
  const [showModalInvite, setShowModalInvite] = React.useState(false);
  const [showFav, setShowFav] = React.useState(false);

  const [showTable, setShowTable] = React.useState(
    project.status == ProjectStatus.PUBLISHED
  );
  const isHourly = React.useMemo(() => project?.type == "hourly", [project]);

  const theme = useTheme();
  const borderColor =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(0, 0, 0, 0.08)";
  const headerGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`;
  const rowHoverBg =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.16)
      : alpha(theme.palette.primary.main, 0.08);
  const subtleTextColor =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.7)"
      : "rgba(0, 0, 0, 0.6)";
  const actionButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    transition: "all 0.25s ease",
    boxShadow: "none",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
    [theme.breakpoints.down("sm")]: {
      px: 1,
      fontSize: "0.75rem",
      py: 0.5,
    },
  } as const;
  const headCellSx = {
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: { xs: "0.6rem", sm: "0.75rem" },
    letterSpacing: { xs: "0.04em", sm: "0.08em" },
    borderBottom: "none",
    color:
      theme.palette.mode === "dark"
        ? theme.palette.grey[100]
        : theme.palette.grey[700],
    py: { xs: 0.75, sm: 2 },
    px: { xs: 0.5, sm: 2 },
    lineHeight: { xs: 1.2, sm: 1.5 },
    whiteSpace: { xs: "normal", sm: "nowrap" },
    wordBreak: { xs: "break-word", sm: "normal" },
    overflow: { xs: "hidden", sm: "visible" },
    textOverflow: { xs: "ellipsis", sm: "clip" },
  } as const;

  const canceledCount = React.useMemo(() => canceledJobs?.length ?? 0, [canceledJobs]);

  const [doHire] = useHireTalentMutation();
  const [doAccept] = useAcceptBidMutation();

  const isAcceptingBid = React.useMemo(
    () => project.status == ProjectStatus.PUBLISHED,
    [project]
  );

  const handleHire = async () => {
    try {
      const job = hireModal.job;
      if (!job) {
        toast.error("Invalid Work");
        return;
      }
      if (job.status == NegotiationStatus.NEW) {
        await doAccept({ projectId: project.id, jobId: job.id }).unwrap();
      }
      if (isHourly)
        await doHire({
          projectId: project.id,
          jobId: job.id,
          rate: job.rate,
          max_hour: job.max_hour,
        }).unwrap();
      else
        await doHire({
          projectId: project.id,
          jobId: job.id,
          price: job.price,
        }).unwrap();
    } catch (err: any) {
      toast.error(err);
    }
  };

  return (
    <Box>
      <CardBase
        nested={true}
        title="Cancelled Offers"
        subtitle={
          canceledCount > 0
            ? `${canceledCount} cancelled offer${canceledCount === 1 ? "" : "s"}`
            : "No cancelled offers"
        }
        useGlassCard
        headerActions={
          <IconButton
            onClick={() => setShowTable(!showTable)}
            sx={{
              borderRadius: "10px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: rowHoverBg,
              },
            }}
          >
            {showTable ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
        sx={{
          width: "100%",
          p: 0,
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 18px 48px rgba(0, 0, 0, 0.5)"
                : "0 18px 48px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        {showTable && (
          <Box sx={{ px: 0, py: { xs: 0, md: 3 } }}>
            <GlassTable maxHeight={{ xs: 420, md: 560 }} hasData={canceledCount > 0}>
              <Table
                stickyHeader
                aria-label="cancelled-offers"
                sx={{
                  minWidth: { xs: 300, sm: 650 },
                  width: "100%",
                  "& .MuiTableHead-root .MuiTableRow-root": {
                    background: headerGradient,
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    ...headCellSx,
                  },
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    borderBottom: `1px solid ${borderColor}`,
                    fontSize: { xs: "0.7rem", sm: "0.95rem" },
                    color: theme.palette.text.primary,
                    py: { xs: 1, sm: 2 },
                    px: { xs: 0.5, sm: 2 },
                    verticalAlign: "top",
                  },
                  "& .MuiTableBody-root .MuiTableRow-root:hover": {
                    backgroundColor: rowHoverBg,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      maxWidth: { xs: 140, sm: "none" }, 
                      minWidth: { xs: 120, sm: "auto" } 
                    }}>
                      Independent Professional
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      width: { xs: 70, sm: "auto" }, 
                      minWidth: { xs: 60, sm: "auto" } 
                    }}>
                      Price
                    </TableCell>
                    {isHourly && (
                      <TableCell align="center" sx={{ 
                        width: { xs: 70, sm: "auto" }, 
                        minWidth: { xs: 60, sm: "auto" } 
                      }}>
                        Max Hour
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ 
                      width: { xs: 100, sm: "auto" }, 
                      minWidth: { xs: 90, sm: "auto" } 
                    }}>
                      Start
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      maxWidth: { xs: 200, sm: "none" }, 
                      minWidth: { xs: 100, sm: "auto" } 
                    }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      width: { xs: 100, sm: "auto" }, 
                      minWidth: { xs: 90, sm: "auto" } 
                    }}>
                      Updated
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      minWidth: { xs: 150, sm: "auto" }, 
                      width: { xs: 150, sm: "auto" } 
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {canceledCount === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={isHourly ? 7 : 6}
                        sx={{
                          textAlign: "center",
                          py: { xs: 4, sm: 6 },
                          color: subtleTextColor,
                          fontSize: { xs: "0.7rem", sm: "0.95rem" },
                        }}
                      >
                        No cancelled offers.
                      </TableCell>
                    </TableRow>
                  )}
                  {canceledJobs?.map((job) => {
                    const status = getTalentBidStatus(job.status);
                    return (
                      <TableRow
                        key={job.id}
                        sx={{
                          "&:last-child .MuiTableCell-root": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell sx={{ width: { xs: 140, md: 260 } }}>
                          <Stack direction="row" spacing={{ xs: 0.5, sm: 1.5 }} alignItems="center">
                            <Box>
                              <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" flexWrap="wrap">
                                <Link
                                  component="button"
                                  onClick={() => {
                                    setSelectedTalent(job.resource || null);
                                    setDrawerOpen(true);
                                  }}
                                  sx={{
                                    fontSize: { xs: "0.75rem", sm: "1rem" },
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  {job.resource?.full_name || "N/A"}
                                </Link>
                                <Rating
                                  value={job.resource?.total_rating ?? 0}
                                  readOnly
                                  precision={0.5}
                                  size="small"
                                  sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                                />
                              </Stack>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: subtleTextColor,
                                  display: "block",
                                  mt: 0.25,
                                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                }}
                              >
                                {[
                                  job.resource?.city,
                                  job.resource?.state,
                                  job.resource?.zip
                                ].filter(Boolean).join(", ") || "Location not available"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                          {job.status == NegotiationStatus.INVITED
                            ? "n/a"
                            : isHourly
                              ? job.rate != null ? `$${job.rate}/hr` : "-"
                              : job.price != null ? `$${job.price}` : "-"}
                        </TableCell>
                        {isHourly && (
                          <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                            {job.max_hour != null ? `${job.max_hour} hr` : "-"}
                          </TableCell>
                        )}
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            component="span"
                            sx={{ 
                              fontFamily: "monospace", 
                              fontSize: { xs: "0.7rem", sm: "0.875rem" } 
                            }}
                          >
                            {job.start_date && job.timezone
                              ? formatWithTimezone(job.start_date, job.timezone)
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ maxWidth: { xs: 200, sm: 320 } }}>
                          <Stack spacing={{ xs: 0.5, sm: 1 }} alignItems="center">
                          <Chip
                            label={status.label}
                            color={status.color}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              borderRadius: "8px",
                              px: 0.75,
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                height: { xs: 20, sm: 24 },
                            }}
                          />
                          {status.label == "CANCELED" && job.canceled_reason && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                  mt: 0.5,
                                color: theme.palette.error.main,
                                fontWeight: 500,
                                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              }}
                            >
                              {job.canceled_reason}
                            </Typography>
                          )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                              color: subtleTextColor,
                            }}
                          >
                            {job.updated_at ? dayjs(job.updated_at).format("MMM DD, YYYY hh:mm A z") : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: { xs: 150, sm: 280 } }}>
                          {isAcceptingBid &&
                            job.status != NegotiationStatus.INVITED &&
                            job.status != NegotiationStatus.CANCELED && (
                              <Stack
                                direction="row"
                                spacing={{ xs: 0.5, sm: 1 }}
                                justifyContent="flex-end"
                                alignItems="center"
                                flexWrap="wrap"
                                rowGap={{ xs: 0.5, sm: 1 }}
                              >
                                <Button
                                  variant="contained"
                                  color="primary"
                                  sx={actionButtonSx}
                                  onClick={() =>
                                    setHireModal({ visible: true, job })
                                  }
                                >
                                  Hire
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="info"
                                  sx={actionButtonSx}
                                  onClick={() => {
                                    if (job?.resource?.email && user?.company.name) {
                                      const subject = `Counter Offer from "${user.company.name}" for Work "${project.title}"!`;
                                      const body = `${user.company.name} will like to counter offer your bid of $${project.type === "hourly"
                                        ? job.rate
                                        : job.price
                                        } to: $\n\nIf you agree to our counter offer, please change your bid directly in the app.\n\nKind Regards,\n\n"${user.company.name}"\n${project?.manager?.first_name} ${project?.manager?.last_name}\n`;
                                      window.location.href = `mailto:${job.resource.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                                    }
                                  }}
                                >
                                  Negotiate
                                </Button>
                              </Stack>
                            )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </GlassTable>
          </Box>
        )}
      </CardBase>
      <TalentDrawer
        forceRefresh={true}
        data={selectedTalent}
        open={drawerOpen}
        setOpen={setDrawerOpen}
      />
      <ConfirmModal
        open={hireModal.visible}
        title={`Hire ${hireModal?.job?.resource?.first_name || ""} ${hireModal?.job?.resource?.last_name || ""
          }?`}
        onClose={() => setHireModal({ visible: false, job: null })}
        onConfirm={handleHire}
      />
      <ModalInvite
        open={showModalInvite}
        onClose={() => {
          setShowFav(false);
          setShowModalInvite(false);
        }}
        project={project}
        showFav={showFav}
        setShowFav={setShowFav}
      />
    </Box>
  );
};
export default TableCancel;
